import { FC, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useSelector } from 'react-redux';

import { BottomSheet } from '@beeline/design-system-react/extracted-styles';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import { BottomSheetTitle } from 'src/components/BottomSheetTitle/BottomSheetTitle';
import {
  BuildingSelect,
  BuildingSelectItem,
  toBuilding,
  toBuildingSelectItem,
} from 'src/components/BuildingSelect/BuildingSelect';
import { DeskAttributes } from 'src/components/DeskAttributes/DeskAttributes';
import {
  FloorSelect,
  FloorSelectItem,
  toFloor,
  toFloorSelectItem,
} from 'src/components/FloorSelect/FloorSelect';
import { Section } from 'src/components/Section/Section';
import {
  toTown,
  toTownSelectItem,
  TownSelect,
  TownSelectItem,
} from 'src/components/TownSelect/TownSelect';
import { useHost } from 'src/providers/HostProvider';
import { getDesksFiltersCount, getDesksState } from 'src/store/desks/selectors';
import { desksActions } from 'src/store/desks/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { noop } from 'src/utils/noop';

import { DeskBookingSettings } from '../DeskBookingSettings/DeskBookingSettings';
import { DeskSearch } from '../DeskSearch/DeskSearch';

import classes from './DeskMapSettingsBottomSheet.module.scss';

const filterFloorOptions = (option: FloorSelectItem): boolean =>
  !option.value.isParking;

const Inner: FC = () => {
  const dispatch = useAppDispatch();

  const handleChangeTown = useCallback(
    (value: TownSelectItem | undefined) => {
      dispatch(desksActions.setTown(toTown(value)));
      dispatch(desksActions.setBuilding(undefined));
      dispatch(desksActions.setFloor(undefined));
    },
    [dispatch],
  );

  const handleChangeBuilding = useCallback(
    (value: BuildingSelectItem | undefined) => {
      dispatch(desksActions.setBuilding(toBuilding(value)));
      dispatch(desksActions.setFloor(undefined));
    },
    [dispatch],
  );

  const handleChangeFloor = useCallback(
    (value: FloorSelectItem | undefined) => {
      dispatch(desksActions.setFloor(toFloor(value)));
    },
    [dispatch],
  );

  const handleChangeByWindow = useCallback(
    (value: boolean) => {
      dispatch(desksActions.setByWindowFilter(value));
    },
    [dispatch],
  );

  const handleChangeWithMonitor = useCallback(
    (value: boolean) => {
      dispatch(desksActions.setWithMonitorFilter(value));
    },
    [dispatch],
  );

  const handleChangeWithDivider = useCallback(
    (value: boolean) => {
      dispatch(desksActions.setWithDividerFilter(value));
    },
    [dispatch],
  );

  const { town, building, floor, filters } = useSelector(getDesksState);
  const filtersCount = useSelector(getDesksFiltersCount);

  const townSelectItem = useMemo(() => toTownSelectItem(town), [town]);
  const buildingSelectItem = useMemo(
    () => toBuildingSelectItem(building),
    [building],
  );
  const floorSelectItem = useMemo(() => toFloorSelectItem(floor), [floor]);

  return (
    <BottomSheet
      isOpen
      handleSetOpen={noop}
      swiper
      modal={false}
      snapPoints={[70, 0.5, 1]}
      style={{ minHeight: '70px' }}
      className={classes.settings}
      initialSnapPoint={0}
      header={{
        icon: Icons.Settings,
        title: (
          <BottomSheetTitle title='Параметры' filtersCount={filtersCount} />
        ) as unknown as string,
      }}
    >
      <div className={classes.container}>
        <Section title='Дата'>
          <DeskBookingSettings />
        </Section>

        <Section title='Местоположение'>
          <TownSelect town={townSelectItem} onChange={handleChangeTown} />
          <BuildingSelect
            townId={town?.townId}
            building={buildingSelectItem}
            onChange={handleChangeBuilding}
            key={town?.townId}
          />
          <FloorSelect
            buildingId={building?.buildingId}
            floor={floorSelectItem}
            onChange={handleChangeFloor}
            filterOptions={filterFloorOptions}
            key={building?.buildingId}
          />
        </Section>

        <Section title='Атрибуты рабочего места'>
          <DeskAttributes
            byWindow={filters.byWindow}
            onChangeByWindow={handleChangeByWindow}
            withMonitor={filters.withMonitor}
            onChangeWithMonitor={handleChangeWithMonitor}
            withDivider={filters.withDivider}
            onChangeWithDivider={handleChangeWithDivider}
          />
        </Section>
        <Section title='Поиск по номеру'>
          <DeskSearch />
        </Section>
      </div>
    </BottomSheet>
  );
};

export const DeskMapSettingsBottomSheet: FC = () => {
  const { host } = useHost();

  return host ? createPortal(<Inner />, host) : null;
};
